import { useState, useEffect, useCallback, useRef } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const notificationsRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const handleNotificationClick = useCallback(async (notification) => {
    try {
      const token = localStorage.getItem("auth_token");
      
      // Mark as read
      await axios.put(
        `${getBaseURL()}/api/notifications/${notification.id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Navigate based on notification type
      if (notification.type === "support_ticket" && notification.related_id) {
        navigate("/support-tickets");
      }

      // Remove from list
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setShowDropdown(false);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, [navigate]);

  useEffect(() => {
    // Poll for notifications every 10 seconds
    const pollNotifications = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const response = await axios.get(
          `${getBaseURL()}/api/notifications/unread`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const newNotifications = response.data;
        const currentNotifications = notificationsRef.current;
        
        // Check if there are new notifications
        if (newNotifications.length > currentNotifications.length) {
          // Show toast for new notifications
          const newOnes = newNotifications.filter(
            n => !currentNotifications.find(old => old.id === n.id)
          );
          
          newOnes.forEach(notif => {
            toast.info(notif.title, {
              description: notif.message,
              duration: 5000,
              action: {
                label: "View",
                onClick: () => handleNotificationClick(notif)
              }
            });
          });
        }
        
        setNotifications(newNotifications);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    pollNotifications();
    const interval = setInterval(pollNotifications, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [handleNotificationClick]);

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${getBaseURL()}/api/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications([]);
      setShowDropdown(false);
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <Bell className="h-6 w-6" />
        {notifications.length > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {notifications.length}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200">
          <div className="py-2">
            <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No new notifications
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {notification.title}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {notification.message}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(notification.created_date).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
